import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

// Country → currency mapping (ISO 4217)
const CURRENCY_BY_COUNTRY = {
  US: { code: 'USD', symbol: '$',  locale: 'en-US' },
  GB: { code: 'GBP', symbol: '£',  locale: 'en-GB' },
  DE: { code: 'EUR', symbol: '€',  locale: 'de-DE' },
  FR: { code: 'EUR', symbol: '€',  locale: 'fr-FR' },
  ES: { code: 'EUR', symbol: '€',  locale: 'es-ES' },
  IN: { code: 'INR', symbol: '₹',  locale: 'en-IN' },
  BR: { code: 'BRL', symbol: 'R$', locale: 'pt-BR' },
  JP: { code: 'JPY', symbol: '¥',  locale: 'ja-JP' },
  CA: { code: 'CAD', symbol: 'C$', locale: 'en-CA' },
  AU: { code: 'AUD', symbol: 'A$', locale: 'en-AU' },
};

// Reverse map: currency code → representative country code. Used to apply
// the user's saved `default_display_currency` preference (e.g. "INR" → "IN").
const COUNTRY_BY_CURRENCY = {
  USD: 'US', GBP: 'GB', EUR: 'DE', INR: 'IN', BRL: 'BR',
  JPY: 'JP', CAD: 'CA', AUD: 'AU',
};

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sensible fallback rates if the exchange-rate API is unreachable (roughly Feb-2026).
// These are only used until the live fetch resolves; formatting will refresh once rates arrive.
const FALLBACK_RATES = {
  USD: 1, GBP: 0.79, EUR: 0.92, INR: 87.0, BRL: 5.9, JPY: 156.0, CAD: 1.42, AUD: 1.55,
};

const STORAGE_COUNTRY = 'tokengate_country';
const STORAGE_RATES = 'tokengate_fx_rates_v1';
const RATES_TTL_MS = 12 * 60 * 60 * 1000; // 12h

const CurrencyContext = createContext(null);

export const CurrencyProvider = ({ children }) => {
  const { user } = useAuth();
  const [countryCode, setCountryCode] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_COUNTRY) || 'US';
    } catch { return 'US'; }
  });
  const [rates, setRates] = useState(FALLBACK_RATES);
  const [ratesFetchedAt, setRatesFetchedAt] = useState(0);

  // Persist country choice
  useEffect(() => {
    try { localStorage.setItem(STORAGE_COUNTRY, countryCode); } catch {}
  }, [countryCode]);

  // Whenever the user logs in (or `user.id` changes), pull their saved display
  // currency preference and apply it. Silent if no pref set. Non-fatal on error.
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    axios.get(`${API_URL}/user/preferences`).then(({ data }) => {
      const saved = data?.default_display_currency;
      if (cancelled || !saved) return;
      const targetCountry = COUNTRY_BY_CURRENCY[saved];
      if (targetCountry) setCountryCode(targetCountry);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [user?.id]);

  // Load cached rates on mount + refresh from API (free, no key)
  useEffect(() => {
    try {
      const cached = localStorage.getItem(STORAGE_RATES);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed?.rates && Date.now() - (parsed.ts || 0) < RATES_TTL_MS) {
          setRates({ ...FALLBACK_RATES, ...parsed.rates });
          setRatesFetchedAt(parsed.ts);
          return;
        }
      }
    } catch {}
    fetch('https://open.er-api.com/v6/latest/USD')
      .then(r => r.json())
      .then(data => {
        if (data?.result === 'success' && data?.rates) {
          const filtered = {};
          Object.keys(FALLBACK_RATES).forEach(k => {
            if (data.rates[k]) filtered[k] = data.rates[k];
          });
          setRates({ ...FALLBACK_RATES, ...filtered });
          setRatesFetchedAt(Date.now());
          try {
            localStorage.setItem(STORAGE_RATES, JSON.stringify({ ts: Date.now(), rates: filtered }));
          } catch {}
        }
      })
      .catch(() => {}); // fallback rates already in state
  }, []);

  const currency = CURRENCY_BY_COUNTRY[countryCode] || CURRENCY_BY_COUNTRY.US;
  const rate = rates[currency.code] || 1;

  // Format any USD amount into the currently selected currency
  const formatPrice = useCallback((usd, opts = {}) => {
    const { showCode = false, decimals } = opts;
    if (usd == null || isNaN(usd)) return '';
    const converted = Number(usd) * rate;
    // For zero-decimal currencies (JPY, INR is small denomination), use 0 decimals when >= 100
    const inferredDecimals =
      decimals != null
        ? decimals
        : (currency.code === 'JPY' ? 0 : (converted >= 100 ? 0 : 2));
    try {
      const nf = new Intl.NumberFormat(currency.locale, {
        style: 'currency',
        currency: currency.code,
        minimumFractionDigits: inferredDecimals,
        maximumFractionDigits: inferredDecimals,
      });
      const formatted = nf.format(converted);
      return showCode ? `${formatted} ${currency.code}` : formatted;
    } catch {
      return `${currency.symbol}${converted.toFixed(inferredDecimals)}`;
    }
  }, [rate, currency]);

  // Format explicitly in USD (used for the transparency note near checkout / payment)
  const formatUsd = useCallback((usd) => {
    if (usd == null || isNaN(usd)) return '';
    return `$${Number(usd).toFixed(2)} USD`;
  }, []);

  const setCountry = useCallback((code) => setCountryCode(code), []);

  const value = {
    countryCode,
    setCountry,
    currency,
    rate,
    ratesFetchedAt,
    formatPrice,
    formatUsd,
    isUsd: currency.code === 'USD',
  };

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
};

export const useCurrency = () => {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error('useCurrency must be used inside <CurrencyProvider>');
  return ctx;
};
